/**
 * RISK-IMPORT
 * Upload existing document
 */

import StepCard from "@/components/step-card"
import { faColumns, faTag, faUpload } from "@fortawesome/free-solid-svg-icons"
import { StepAlert } from "@/redux/project.types"
import { useState } from "react"
import { RisksImportUpload } from "./RisksImportUpload"
import RiskImportModal from "./modals/RiskImportModal"
import { RiskMapping } from "../model/risk.types"
import { RisksImportMapping } from "./RisksImportMapping"
import { RisksImportOrder } from "./RisksImportOrder"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import establishmentStore from "@/features/adm/entities/establishment/model/establishment.store"
import unitStore from "../../unit/model/unit.store"
import riskStore from "../model/risk.store"
import { fetchBackend } from "@/core/apis/backend"
import LoadingModal from "@/modals/loading.modal"
import { useNavigate } from "react-router-dom"

const MODAL_IMPORT = "MODAL_IMPORT"

export function RisksImport() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // selectors
  const organization = useAppSelector(organizationStore.selectors.active)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const risks = useAppSelector(riskStore.selectors.list)

  // List of attributes in the order
  const [attributes, setAttributes] = useState<string[]>([])

  // Current step of the wizard
  const [currentStep, setCurrentStep] = useState<null | string>("upload")

  // Defined which steps are confirmed
  const [confirmedSteps, setConfirmedSteps] = useState<string[]>([])

  // Mapping displayed
  const [mapping, setMapping] = useState<RiskMapping>({ importColumns: [], rows: [], units: [], risks: [] })

  // Modal displayed
  const [modal, setModal] = useState<string | null>(null)

  const [isImporting, setIsImporting] = useState(false)

  async function runImport(replace: boolean, att: string[], currentMapping: RiskMapping) {
    setIsImporting(true)
    dispatch(riskStore.actions.setStatus("STATUS_SAVING"))

    const response = await fetchBackend({
      url: "/risks/import",
      method: "POST",
      data: {
        establishmentId: establishment.id,
        risks: currentMapping.risks,
        units: currentMapping.units,
        replace,
        riskAttributes: att
      }
    })

    if (response.error) {
      dispatch(riskStore.actions.setStatus("STATUS_SAVE_ERROR"))
      setIsImporting(false)
    } else {
      dispatch(organizationStore.actions.setActive({ ...organization, riskAttributes: att }))
      dispatch(establishmentStore.actions.setActive(establishment))
      await dispatch(unitStore.thunks.fetchAndSet())
      await dispatch(riskStore.thunks.fetchValidatedAndSet(null))
      dispatch(riskStore.actions.setStatus("STATUS_SAVED"))
      navigate("/duerp")
    }
  }

  // Steps of the wizard
  const steps = [
    {
      id: "upload",
      title: "Importer mon document",
      icon: faUpload,
      component: (
        <RisksImportUpload
          onUpload={(m: RiskMapping) => {
            setMapping(m)
            setCurrentStep("mapping")
            setConfirmedSteps(["upload"])
          }}
        />
      )
    },
    {
      id: "mapping",
      title: "Définir les colonnes",
      icon: faColumns,
      component: (
        <RisksImportMapping
          mapping={mapping}
          onConfirm={(m: RiskMapping) => {
            setMapping(m)
            setCurrentStep("order")
            setConfirmedSteps(["upload", "mapping"])
          }}
        />
      )
    },
    {
      id: "order",
      title: (
        <span className="flex items-center gap-3">
          {"Définir l'ordre des colonnes"}
          <span className="grey-t" style={{ fontSize: 12, fontWeight: "normal" }}>
            {"(glisser-déposer pour réordonner)"}
          </span>
        </span>
      ),
      icon: faTag,
      component: (
        <RisksImportOrder
          mapping={mapping}
          onConfirm={(att: string[]) => {
            dispatch(organizationStore.actions.edit({ key: "riskAttributes", value: organization.riskAttributes }))
            setAttributes(att)
            setConfirmedSteps(["upload", "mapping", "order"])
            if (risks.length === 0) {
              runImport(false, att, mapping)
            } else {
              setModal(MODAL_IMPORT)
            }
          }}
        />
      )
    }
  ]

  return (
    <div>
      {isImporting && <LoadingModal owlAssistantText="Import en cours, veuillez patienter..." />}

      {modal === MODAL_IMPORT && (
        <RiskImportModal
          mapping={mapping}
          attributes={attributes}
          onClose={() => setModal(null)}
          onImport={(replace: boolean) => {
            setModal(null)
            runImport(replace, attributes, mapping)
          }}
        />
      )}

      {steps.map((step) => (
        <StepCard
          key={step.id}
          title={step.title}
          isOpen={step.id === currentStep}
          icon={step.icon}
          onOpen={() => setCurrentStep(step.id === currentStep ? null : step.id)}
          isConfirmed={confirmedSteps.indexOf(step.id) > -1}
          isVisited={confirmedSteps.indexOf(step.id) > -1}
          alert={new StepAlert()}>
          {step.component}
        </StepCard>
      ))}
    </div>
  )
}
